import { sortBy } from 'lodash'
import queryString from 'query-string'

import { fetchingAPI, apiService } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { getTagsWithCategories, getDateRangeFromSavedFilter } from '@/utils/helpers'
import { fetchVoipCampaignsAPI } from '@/reducers/callSearch/callSearch.actions'
import { canListOrganizations } from '@/reducers/organizations/organizations.actions'
import {
  COPILOT_FILTER_TYPE,
  PROMPT_ENGINEERING_FILTER_TYPE,
  QA_ACTIVITY_FILTER_TYPE,
} from '@/reducers/savedFilters/savedFilters.constants'

import { setData, setLoading } from '@/reducers/callSearch/callSearch.redux'
import * as redux from './savedFilters.redux'
import { formatSavedFilters } from './savedFilters.helpers'
import { closeModal } from '../ui/ui.redux'

/*
  This is extracted into a helper function so action creators waiting
  on savedFilters to load can retrieve them from state.
*/
export const fetchSavedFiltersHelper = async (dispatch, filterType) => {
  dispatch(redux.setLoading({ [filterType]: true }))

  try {
    const response = await fetchingAPI(
      `${apiService.core}/api/reporting/saved_filters?filter_type=${filterType}`,
      'GET',
      dispatch
    )
    const filters = sortBy(response.saved_filters, [(filter) => !filter.is_default, 'name'])
    filters.forEach((filter) => {
      if (filter.filters?.dispositions?.filters?.length) {
        filter.filters.dispositions = {
          ...filter.filters.dispositions,
          selected: filter.filters.dispositions.filters,
        }
      }
    })

    dispatch(redux.setFilterList({ [filterType]: filters }))
  } catch (err) {
    toast.error('Failed to retrieve saved filters', { cause: err })
  } finally {
    dispatch(redux.setLoading({ [filterType]: false }))
  }
}

export const fetchSavedFilters = (filterType) => async (dispatch) => {
  await fetchSavedFiltersHelper(dispatch, filterType)
}

export const createSavedFilter = (filter, filterType) => async (dispatch, getState) => {
  const { currentUser } = getState()
  const { organizationid: currentUserOrgId } = currentUser
  const filterToSave = formatSavedFilters(filter, currentUserOrgId, filterType)
  try {
    await fetchingAPI(
      `${apiService.core}/api/reporting/saved_filters`,
      'POST',
      dispatch,
      JSON.stringify(filterToSave)
    )

    toast.success('Filter created')
    dispatch(closeModal())
    dispatch(fetchSavedFilters(filterType))
  } catch {
    toast.error('Failed to save filters')
  }
}

export const updateSavedFilter = (uuid, filter, filterType) => async (dispatch, getState) => {
  const { currentUser } = getState()
  const { organizationid: currentUserOrgId } = currentUser
  const filterToSave = formatSavedFilters(filter, currentUserOrgId, filterType)

  try {
    await fetchingAPI(
      `${apiService.core}/api/reporting/saved_filters/${uuid}`,
      'PUT',
      dispatch,
      JSON.stringify(filterToSave)
    )

    toast.success('Filter updated')
    dispatch(closeModal())
    dispatch(fetchSavedFilters(filterType))
  } catch (err) {
    toast.error('Failed to update filter', { cause: err })
  }
}

export const deleteSavedFilter = (filterId, filterType) => async (dispatch) => {
  dispatch(redux.setLoading({ [filterType]: true }))

  try {
    await fetchingAPI(
      `${apiService.core}/api/reporting/saved_filters/${filterId}`,
      'DELETE',
      dispatch
    )

    toast.success('Filter deleted')
    dispatch(fetchSavedFilters(filterType))
  } catch (err) {
    toast.error('Failed to delete saved filter', { cause: err })
  } finally {
    dispatch(redux.setLoading({ [filterType]: false }))
  }
}

export const fetchOrganizationsForFilters = () => async (dispatch, getState) => {
  // Staff-only list (see canListOrganizations): for anyone else the request can
  // only 403, which showed a "Failed to fetch organizations" toast.
  if (!canListOrganizations(getState().currentUser)) {
    return
  }

  dispatch(redux.setLoading({ organizations: true }))

  try {
    const organizations = await fetchingAPI(`${apiService.core}/api/organizations`, 'GET', dispatch)

    const organizationOptions = organizations
      .map((org) => ({ value: org.id, label: org.name }))
      .sort((a, b) => a.label.localeCompare(b.label))

    dispatch(redux.setData({ organizations: organizationOptions }))
  } catch (error) {
    toast.error('Failed to fetch organizations', { cause: error })
  } finally {
    dispatch(redux.setLoading({ organizations: false }))
  }
}

export const fetchTagsForFilters = (organizationId) => async (dispatch) => {
  dispatch(redux.setLoading({ tags: true }))

  try {
    const [tags, tagCategories] = await Promise.all([
      fetchingAPI(`${apiService.core}/api/organizations/${organizationId}/tags`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags/categories`,
        'GET',
        dispatch
      ),
    ])

    const tagsOptionsWithCategories = getTagsWithCategories(tags, tagCategories)

    dispatch(redux.setData({ tags: tagsOptionsWithCategories }))
  } catch (err) {
    toast.error('Failed to fetch tags', { cause: err })
  } finally {
    dispatch(redux.setLoading({ tags: false }))
  }
}

export const fetchAgentsForFilters = (organizationId) => async (dispatch) => {
  dispatch(redux.setLoading({ agents: true }))

  try {
    const { users } = await fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/users`,
      'GET',
      dispatch
    )

    users.sort((a, b) => a.first_name.localeCompare(b.first_name))
    const agentOptions = users.map((agent) => ({
      value: agent.id,
      label: `${agent.first_name} ${agent.last_name}`,
    }))

    dispatch(redux.setData({ agents: agentOptions }))
  } catch (err) {
    toast.error('Failed to fetch agents', { cause: err })
  } finally {
    dispatch(redux.setLoading({ agents: false }))
  }
}

export const fetchPlaybooksForFilters = (organizationId) => async (dispatch) => {
  dispatch(redux.setLoading({ playbooks: true }))

  try {
    const configProperties = queryString.stringify({ requested_properties: 'id,name,cid' })
    const playbooks = await fetchingAPI(
      `${apiService.core}/api/${organizationId}/configs?${configProperties}`,
      'GET',
      dispatch
    )
    const playbookOptions = playbooks
      .map((playbook) => ({ value: playbook.cid, label: playbook.name }))
      .sort((a, b) => a.label.localeCompare(b.label))

    dispatch(redux.setData({ playbooks: playbookOptions }))
  } catch (err) {
    toast.error('Failed to fetch playbooks', { cause: err })
  } finally {
    dispatch(redux.setLoading({ playbooks: false }))
  }
}

export const fetchPlaylistsForFilters = (organizationId) => async (dispatch) => {
  dispatch(redux.setLoading({ playlists: true }))

  try {
    const params = queryString.stringify({ requested_organization_id: organizationId })
    const playlists = await fetchingAPI(
      `${apiService.workflow}/playlists?${params}`,
      'GET',
      dispatch
    )
    const playlistOptions = (playlists || [])
      .map((playlist) => ({ value: playlist.id, label: playlist.name }))
      .sort((a, b) => a.label.localeCompare(b.label))

    dispatch(redux.setData({ playlists: playlistOptions }))
  } catch (err) {
    toast.error('Failed to fetch playlists', { cause: err })
  } finally {
    dispatch(redux.setLoading({ playlists: false }))
  }
}

export const fetchCategoriesForFilters =
  (organizationId, section, playbooks = []) =>
  async (dispatch) => {
    dispatch(redux.setLoading({ [`${section}Categories`]: true }))

    try {
      const filters = queryString.stringify({
        organization_id: organizationId,
        playbooks: playbooks.map((playbook) => playbook.value),
      })
      const categories = await fetchingAPI(
        `${apiService.core}/api/configs/${section}_categories?${filters}`,
        'GET',
        dispatch
      )
      const categoryOptions = categories[`${section}_categories`]
        .sort((a, b) => a.localeCompare(b))
        .map((cat) => ({ value: cat, label: cat }))

      dispatch(redux.setData({ [`${section}Categories`]: categoryOptions }))
    } catch (err) {
      toast.error('Failed to fetch categories', { cause: err })
    } finally {
      dispatch(redux.setLoading({ [`${section}Categories`]: false }))
    }
  }
export const fetchDispositionsOptionsForFilters = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ dispositions: true }))

  try {
    const dispositions = await fetchingAPI(
      `${apiService.reporting}/api/dispositions?organization_id=${organizationId}`,
      'GET',
      dispatch
    )
    const dispoOptions = dispositions
      .map(({ name }) => ({ value: name, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
    dispatch(setData({ dispositionsOptions: dispoOptions }))
  } catch (err) {
    toast.error('Failed to fetch dispositions', { cause: err })
  } finally {
    dispatch(setLoading({ dispositions: false }))
  }
}
export const fetchScorecardForFilters =
  (organizationId, scorecardType = null) =>
  async (dispatch) => {
    dispatch(redux.setLoading({ scorecards: true }))
    const params = new URLSearchParams()
    params.append('requested_organization_id', organizationId)
    if (scorecardType) {
      params.append('scorecard_type', scorecardType)
    }

    const url = `${apiService.scorecard}/scoring/scorecards/current?${params.toString()}`

    try {
      const apiScorecardConfigs = await fetchingAPI(url, 'GET', dispatch)
      const sortedScorecardConfigs = apiScorecardConfigs
        .sort((a, b) => a.name?.localeCompare(b.name))
        .map((scorecard) => ({
          value: scorecard.id,
          label: scorecard.name,
          sid: scorecard.sid,
        }))

      dispatch(redux.setData({ scorecardConfigs: sortedScorecardConfigs }))
    } catch (err) {
      toast.error(`Failed to fetch scorecards for organization: ${organizationId}`, { cause: err })
    } finally {
      dispatch(redux.setLoading({ scorecards: false }))
    }
  }

export const fetchVoipCampaignsForFilters =
  (organizationId, dateRange = 'today') =>
  async (dispatch) => {
    dispatch(redux.setLoading({ voipCampaigns: true }))

    try {
      const { startDate, endDate } = getDateRangeFromSavedFilter(dateRange)
      const voipCampaignOptions = await fetchVoipCampaignsAPI(
        organizationId,
        startDate,
        endDate,
        dispatch
      )

      dispatch(redux.setData({ voipCampaigns: voipCampaignOptions }))
    } catch (err) {
      toast.error('Failed to fetch VOIP campaigns', { cause: err })
    } finally {
      dispatch(redux.setLoading({ voipCampaigns: false }))
    }
  }

export const fetchFilterFormData =
  (organizationId, filterFormOptions, selectedFilter, filterType = null) =>
  async (dispatch) => {
    const dateRange = selectedFilter?.filters?.dateRange || 'today'

    if (filterFormOptions.includes('organizations')) {
      dispatch(fetchOrganizationsForFilters())
    }

    if (filterFormOptions.includes('agents')) {
      dispatch(fetchAgentsForFilters(organizationId))
    }

    if (filterFormOptions.includes('tags')) {
      dispatch(fetchTagsForFilters(organizationId))
    }

    if (filterFormOptions.includes('playbooks')) {
      dispatch(fetchPlaybooksForFilters(organizationId))
    }

    if (filterFormOptions.includes('voipCampaigns')) {
      dispatch(fetchVoipCampaignsForFilters(organizationId, dateRange))
    }

    if (filterFormOptions.includes('dispositions')) {
      dispatch(fetchDispositionsOptionsForFilters(organizationId))
    }
    if (filterFormOptions.includes('playlists')) {
      dispatch(fetchPlaylistsForFilters(organizationId))
    }
    if (filterFormOptions.includes('scorecards')) {
      if (filterType === COPILOT_FILTER_TYPE || filterType === PROMPT_ENGINEERING_FILTER_TYPE) {
        dispatch(fetchScorecardForFilters(organizationId, 'copilot'))
      } else if (filterType === QA_ACTIVITY_FILTER_TYPE) {
        dispatch(fetchScorecardForFilters(organizationId, 'automated'))
      } else {
        dispatch(fetchScorecardForFilters(organizationId))
      }
    }
  }
